import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { Plug, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { postApiProvidersDescriptors } from "@/lib/api/generated/providers/providers";
import { getApiCapabilityBindings } from "@/lib/api/generated/capabilities/capabilities";
import { useGetApiConnectorInstalls } from "@/lib/api/generated/channels/channels";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { ROUTES } from "@/app/routes";
import { queryKeys } from "@/lib/query/keys";
import { getInstallableProviderDescriptors } from "./provider-descriptor-helpers";
import type {
  ConnectorInstallRecord,
  ProviderDescriptorRecord,
  TenantCapabilityBindingRecord,
} from "../../../../../packages/shared-types/src/index.js";

export function ConnectorsPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: installs, isLoading } = useGetApiConnectorInstalls<ConnectorInstallRecord[]>({
    query: { enabled: Boolean(activeTenantId) },
  });
  const { data: providerDescriptors = [], isLoading: providersLoading } = useQuery({
    queryKey: ["providers-descriptors", activeTenantId],
    queryFn: () => postApiProvidersDescriptors({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: bindings } = useQuery({
    queryKey: queryKeys.tenantCapabilityBindings(activeTenantId),
    queryFn: () => getApiCapabilityBindings(),
    enabled: Boolean(activeTenantId),
  });
  useDocumentTitle("Connector Installs");
  const installableProviders = useMemo(
    () => getInstallableProviderDescriptors(providerDescriptors as unknown as ProviderDescriptorRecord[]),
    [providerDescriptors],
  );

  const installHref = ROUTES.tenantConnectorInstallNew(activeTenantId);
  const mcpInstallHref = ROUTES.tenantMcpToolsetNew(activeTenantId);
  const activationsHref = ROUTES.tenantImplementations(activeTenantId);
  const pluginsHref = ROUTES.tenantPlugins(activeTenantId);
  const bindingCountsByInstall = useMemo(() => {
    const counts = new Map<string, number>();
    for (const record of (bindings as TenantCapabilityBindingRecord[] | undefined) ?? []) {
      const connectorInstallId = record.connectorInstallId?.trim();
      if (!connectorInstallId) {
        continue;
      }
      counts.set(connectorInstallId, (counts.get(connectorInstallId) ?? 0) + 1);
    }
    return counts;
  }, [bindings]);
  const installsByConnectorKey = useMemo(() => {
    const counts = new Map<string, number>();
    for (const install of installs ?? []) {
      counts.set(install.connectorKey, (counts.get(install.connectorKey) ?? 0) + 1);
    }
    return counts;
  }, [installs]);

  const header = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1.5">
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--muted-foreground)" }}
          >
            Step 1 of 5
          </span>
          <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            Install connector backends
          </h2>
          <p className="max-w-2xl text-xs" style={{ color: "var(--muted-foreground)" }}>
            Install the real systems this tenant will talk to, then choose which business
            capability each connector powers on the connector setup page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(pluginsHref)}>
            Open plugins
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(activationsHref)}>
            Open connector setup
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(mcpInstallHref)}>
            <Plug className="h-4 w-4 mr-1" /> Register MCP server
          </Button>
          <Button size="sm" onClick={() => navigate(installHref)}>
            <Plus className="h-4 w-4 mr-1" /> Install connector
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <MetricCard
          label="Available connectors"
          value={String(installableProviders.length)}
          description="Connector descriptors available for tenant install."
        />
        <MetricCard
          label="Connector installs"
          value={String(installs?.length ?? 0)}
          description="Installed connectors available for capability mapping."
        />
        <MetricCard
          label="Capability mappings"
          value={String(bindings?.length ?? 0)}
          description="Business capabilities already mapped to a connector."
        />
      </div>
    </div>
  );

  if (isLoading || providersLoading) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}

      <Card>
        <CardHeader className="gap-2">
          <CardTitle>Available Connectors</CardTitle>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            This is the connector catalog exposed by the control plane. Install one or more tenant
            instances, then configure credentials and activate business capabilities against them.
          </p>
        </CardHeader>
        <CardContent>
          {installableProviders.length === 0 ? (
            <EmptyState
              title="No backend descriptors available"
              description="This control-plane instance is not exposing any installable runtime backend providers."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {installableProviders.map((provider) => {
                const connectorKey = provider.connectorKey ?? provider.providerKey;
                const installCount = installsByConnectorKey.get(connectorKey) ?? 0;
                return (
                  <Card key={provider.descriptorKey} className="border-border/80">
                    <CardHeader className="gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col gap-1">
                          <CardTitle>{provider.displayName}</CardTitle>
                          <p
                            className="text-xs font-mono"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            {connectorKey}
                          </p>
                        </div>
                        <StatusBadge status={installCount > 0 ? "active" : "draft"} />
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                        {provider.description || "No description provided for this connector."}
                      </p>
                      <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                        {installCount > 0
                          ? `${installCount} tenant ${installCount === 1 ? "install" : "installs"} configured`
                          : "Not installed for this tenant yet."}
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                        Host: {provider.executionHost}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => navigate(installHref)}>
                          {installCount > 0 ? "Add Another Install" : "Install Connector"}
                        </Button>
                        {installCount > 0 ? (
                          <Button size="sm" variant="outline" onClick={() => navigate(pluginsHref)}>
                            Open plugins
                          </Button>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {!installs || installs.length === 0 ? (
        <EmptyState
          title="No connectors installed"
          description="Install a connector like HubSpot or Jira before you map business capabilities."
          action={{
            label: "Install connector",
            onClick: () => navigate(installHref),
          }}
        />
      ) : null}

      {installs && installs.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {installs.map((install) => {
            const configKeys = Object.keys(install.config);
            const activationCount = bindingCountsByInstall.get(install.id) ?? 0;

            return (
              <Card
                key={install.id}
                className="cursor-pointer hover:border-[var(--border-hover)] transition-colors"
                onClick={() => navigate(ROUTES.tenantConnectorInstall(activeTenantId, install.id))}
              >
                <CardHeader className="gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <CardTitle>{install.connectorKey}</CardTitle>
                      <p
                        className="text-xs font-mono"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {install.id}
                      </p>
                    </div>
                    <StatusBadge status={install.status} />
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span style={{ color: "var(--muted-foreground)" }}>Credentials</span>
                      <p className="font-mono break-all" style={{ color: "var(--text-strong)" }}>
                        {String(install.credentialsRef)}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Activations using it
                      </span>
                      <p style={{ color: "var(--text-strong)" }}>
                        {activationCount} {activationCount === 1 ? "capability" : "capabilities"}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                    {configKeys.length > 0
                      ? `Config keys: ${[...configKeys].sort().join(", ")}`
                      : "No config overrides saved for this backend yet."}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div
      className="rounded-md border px-4 py-4"
      style={{ borderColor: "var(--border)", background: "var(--panel)" }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--muted-foreground)" }}>
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: "var(--text-strong)" }}>
        {value}
      </div>
      <p className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
        {description}
      </p>
    </div>
  );
}
